import { fetchApi } from "@/lib/api";
import { useEvents } from "@/lib/hooks/use-events";
import { useToast } from "@/components/ui/use-toast";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { cn } from "../utils";
import { User } from "./user";
import { useFeatureFlagEnabled } from "@posthog/react";

export type NotificationChannelType = "push" | "sms" | "sse";

interface InAppNotificationData {
  menteeId?: string;
  sender?: {
    id: string;
    name: string;
    avatar?: string;
  };
  mentorshipId?: string;
  sdkUrl?: string;
  originalResponse?: string;
  enhancedResponse?: string;
  // Extended metadata for NEW_TEEN notifications
  mentee?: {
    id: string;
    name: string | null;
    first_name: string | null;
    last_name: string | null;
    birthday?: Date | null;
    pronouns?: string | null;
    redacted_fields?: string[];
    profile?: {
      bio?: string | null;
      interests?: string[];
      struggles?: string[];
      life_experiences?: string[] | null;
      favorite_food?: string;
      video_url?: string | null;
      sex?: string | null;
      race?: string | null;
      redacted_fields?: string[];
      metadata?: {
        interestDetails?: Record<string, string>;
        additionalInfo?: string;
        struggleDetails?: string;
        mentorPreferences?: { [key: string]: any };
      };
    };
    [key: string]: any;
  };
  mentorReplied?: boolean;
  mentorRepliedAt?: Date;
  automatedMessageSent?: boolean;
  automatedMessageSentAt?: Date;
  entityId?: string;
  noteId?: string;
  sharedEntityType?: string;
  ownerName?: string;
  pending_other_user?: boolean;
  // Fields for USER_REPORT notifications
  reportId?: string;
  direction?: string;
  reportedUserId?: string;
  priority?: string;
  // Fields for TRAINING_SIMULATOR_COMPLETED notifications
  progressId?: string;
  mentorId?: string;
  reviewId?: string;
  // Fields for CARE_REVIEW_DUE / CARE_REVIEW_OVERDUE notifications
  careRecordId?: string;
  menteeName?: string;
  reviewType?: "intake" | "day_30" | "quarterly";
  dueAt?: string;
}

export enum NotificationType {
  NEW_TEEN = "NEW_TEEN",
  ESCALATION = "ESCALATION",
  NEW_ESCALATION = "NEW_ESCALATION",
  TEEN_DEACTIVATED = "TEEN_DEACTIVATED",
  NOTE_SHARED = "NOTE_SHARED",
  SESSION = "SESSION",
  REMINDER = "REMINDER",
  MENTOR_MONTHLY_UPDATE = "MENTOR_MONTHLY_UPDATE",
  WHAT_TO_WORK_ON = "WHAT_TO_WORK_ON",
  SHAREABLE_ENTITY_SHARED = "SHAREABLE_ENTITY_SHARED",
  SWAP_ACTION_REQUIRED = "SWAP_ACTION_REQUIRED",
  USER_REPORT = "USER_REPORT",
  NOTE_REMINDER = "NOTE_REMINDER",
  TRAINING_SIMULATOR_COMPLETED = "TRAINING_SIMULATOR_COMPLETED",
  MENTOR_PROFILE_SUBMITTED = "MENTOR_PROFILE_SUBMITTED",
  MENTOR_STORY_SUBMITTED = "MENTOR_STORY_SUBMITTED",
  MENTOR_TIME_OFF_SCHEDULED = "MENTOR_TIME_OFF_SCHEDULED",
  MENTOR_STORY_REJECTED = "MENTOR_STORY_REJECTED",
  MENTOR_STORY_APPROVED = "MENTOR_STORY_APPROVED",
  MENTOR_STORY_REMOVED = "MENTOR_STORY_REMOVED",
  CARE_REVIEW_DUE = "CARE_REVIEW_DUE",
  CARE_REVIEW_OVERDUE = "CARE_REVIEW_OVERDUE",

  // More types will be added in the future (sync it with FE)
}

export const isCareReviewNotification = (type: NotificationType) =>
  type === NotificationType.CARE_REVIEW_DUE ||
  type === NotificationType.CARE_REVIEW_OVERDUE;

export interface InAppNotification {
  id: string;
  user_id: string;
  user: User;
  title: string;
  content: string;
  channels: NotificationChannelType[];
  data?: InAppNotificationData;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  type: NotificationType;
}

export interface NotificationResponse {
  notifications: InAppNotification[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface GetNotificationsParams {
  userId?: string;
  menteeId?: string;
  type?: NotificationType;
  unreadOnly?: boolean;
  limit?: number;
  enabled?: boolean;
}

// Hook for getting notifications - works for both current user and admin viewing other users
export const useNotifications = (params?: GetNotificationsParams) => {
  const {
    userId,
    menteeId,
    type,
    unreadOnly = true,
    limit = 20,
    enabled = true,
  } = params || {};

  return useInfiniteQuery<NotificationResponse>({
    initialPageParam: 1,
    queryKey: ["notifications", userId, menteeId, { unreadOnly, limit, type }],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchApi<NotificationResponse>({
        url: `/v2/notifications`,
        method: "GET",
        params: {
          ...(userId && { userId }),
          ...(menteeId && { menteeId }),
          ...(type && { type }),
          unreadOnly,
          page: pageParam,
          limit,
        },
      });

      if (!response.data) throw new Error("Failed to fetch notifications");

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = Math.ceil(lastPage.pagination.total / limit);
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    enabled: enabled,
  });
};

// Hook for marking a notification as read
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const now = new Date().toISOString();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await fetchApi<InAppNotification>({
        url: `/v2/notifications/${notificationId}/read`,
        method: "POST",
      });

      if (!response.data)
        throw new Error("Failed to mark notification as read");
      return response.data;
    },
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Get all notification queries
      const queries = queryClient.getQueriesData<{
        pages: NotificationResponse[];
        pageParams: unknown[];
      }>({ queryKey: ["notifications"] });

      // Store previous states
      const previousData = new Map();

      queries.forEach(([queryKey, oldData]) => {
        if (!oldData?.pages) return;
        previousData.set(queryKey, oldData);

        const queryParams = queryKey[3] as Record<string, unknown> | undefined;
        const isUnreadOnlyQuery = queryParams?.unreadOnly === true;

        if (isUnreadOnlyQuery) {
          // For unread-only queries, remove the notification from all pages
          const newData = {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.filter(
                (notification) => notification.id !== notificationId,
              ),
              pagination: {
                ...page.pagination,
                total: page.pagination.total - 1,
              },
            })),
          };
          queryClient.setQueryData(queryKey, newData);
        } else {
          // For all notifications queries, just mark as read
          const newData = {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) =>
                notification.id === notificationId
                  ? { ...notification, read_at: now }
                  : notification,
              ),
            })),
          };
          queryClient.setQueryData(queryKey, newData);
        }
      });

      // Update notification count optimistically
      const previousCount = queryClient.getQueryData<{
        data: { count: number };
      }>(["notification-count"]);

      if (previousCount?.data) {
        const newCount = Math.max(0, previousCount.data.count - 1);
        queryClient.setQueryData(["notification-count"], {
          ...previousCount,
          data: { count: newCount },
        });
      }

      return {
        previousData,
        previousCount,
      };
    },
    onError: (_, __, context) => {
      if (!context) return;

      // Restore all queries to their previous state
      context.previousData?.forEach((oldData, queryKey) => {
        queryClient.setQueryData(queryKey, oldData);
      });

      // Restore notification count
      if (context.previousCount) {
        queryClient.setQueryData(["notification-count"], context.previousCount);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notification-count"],
      });
    },
  });
};

export const useNotificationCount = () => {
  const { data } = useQuery({
    queryKey: ["notification-count"],
    queryFn: () =>
      fetchApi<{ count: number }>({ url: "/v2/notifications/count" }),
  });
  return data?.data?.count || 0;
};

export const useNotificationEvents = () => {
  const { toast } = useToast();
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();
  const isMomentsEnabled = useFeatureFlagEnabled("enable-moments");
  const isSwapsEnabled = useFeatureFlagEnabled("enable-swaps");
  const isCareEnabled =
    useFeatureFlagEnabled("enable-care-page") || import.meta.env.DEV;

  useEffect(() => {
    const handleNewNotification = (payload: InAppNotification) => {
      if (isCareReviewNotification(payload.type) && !isCareEnabled) {
        return;
      }

      // Suppress moment/swap notifications if feature flag is off
      if (payload.type === NotificationType.SHAREABLE_ENTITY_SHARED) {
        const data = payload.data as { sharedEntityType?: string } | undefined;
        if (
          !isMomentsEnabled &&
          data?.sharedEntityType?.toLowerCase() === "moment"
        ) {
          return;
        }
        if (
          !isSwapsEnabled &&
          data?.sharedEntityType?.toLowerCase() === "swap"
        ) {
          return;
        }
      }
      if (
        payload.type === NotificationType.SWAP_ACTION_REQUIRED &&
        !isSwapsEnabled
      ) {
        return;
      }

      // Show toast for the new notification
      if (payload.type !== "NEW_TEEN") {
        toast({
          title: payload.title,
          description: payload.content,
          variant: "default",
          className: cn(
            "top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4",
            "border-2 border-primary shadow-lg rounded-lg",
            "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
          ),
          duration: 5 * 1000, // 5 seconds
        });
      }

      // Invalidate notifications queries to refresh the notification drawer
      queryClient.invalidateQueries({
        predicate: (query) =>
          (query.queryKey.includes("notifications") &&
            query.queryKey[1] === payload.user_id) ||
          query.queryKey.includes("notification-count"),
      });

      // Invalidate shareable entities so hub list/detail views update immediately
      if (
        payload.type === NotificationType.SHAREABLE_ENTITY_SHARED ||
        payload.type === NotificationType.SWAP_ACTION_REQUIRED
      ) {
        queryClient.invalidateQueries({
          queryKey: ["shareable-entities"],
          refetchType: "all",
        });
      }
    };

    // Add event listener for new notifications
    const cleanup = addEventListener<InAppNotification>(
      "NOTIFICATION_CREATED",
      handleNewNotification,
    );

    return () => {
      cleanup();
    };
  }, [
    addEventListener,
    toast,
    queryClient,
    isMomentsEnabled,
    isSwapsEnabled,
    isCareEnabled,
  ]);
};

export const getWhatToWorkOnForMentorship = async (
  mentorshipId: string,
): Promise<{ notification: InAppNotification | null }> => {
  const response = await fetchApi<{ notification: InAppNotification | null }>({
    url: `/v2/notifications/mentorship/${mentorshipId}/what-to-work-on`,
    method: "GET",
  });
  return response.data;
};

export const generateSuggestedOpener = async (
  notificationId: string,
): Promise<{ suggestedOpener: string }> => {
  const response = await fetchApi<{ suggestedOpener: string }>({
    url: `/v2/notifications/${notificationId}/generate-opener`,
    method: "POST",
  });
  return response.data;
};

// Hook for getting what-to-work-on notification for a specific mentorship
export const useWhatToWorkOnNotification = (mentorshipId: string | null) => {
  return useQuery({
    queryKey: ["what-to-work-on", mentorshipId],
    queryFn: () =>
      mentorshipId ? getWhatToWorkOnForMentorship(mentorshipId) : null,
    enabled: !!mentorshipId,
  });
};

// Hook for generating suggested opener
export const useGenerateSuggestedOpener = () => {
  return useMutation({
    mutationFn: generateSuggestedOpener,
  });
};

// Hook for getting NEW_TEEN notifications
export const useNewTeenNotifications = () => {
  return useNotifications({
    type: NotificationType.NEW_TEEN,
    unreadOnly: false,
  });
};

// Hook for sending first message to teen
export const useSendFirstMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      notificationId,
      message,
      mentorshipId,
    }: {
      notificationId: string;
      message: string;
      mentorshipId: string;
    }) => {
      const response = await fetchApi({
        url: `/v2/notifications/${notificationId}/send-message`,
        method: "POST",
        data: { message, mentorshipId },
      });

      if (!response.data) {
        throw new Error("Failed to send message - no data in response");
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data from server
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
  });
};
